import { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Disc3 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";

const API = process.env.REACT_APP_BACKEND_URL;

const resolveSrc = (url) => (url && url.startsWith("/api/") ? `${API}${url}` : url);

const fmtTime = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const AudioPlayer = () => {
  const audioRef = useRef(null);
  const [TRACKS, setTracks] = useState([]);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(() => {
    try {
      return localStorage.getItem("nox_muted") === "1";
    } catch {
      return false;
    }
  });
  const [volume, setVolume] = useState(() => {
    try {
      const v = parseFloat(localStorage.getItem("nox_volume"));
      return Number.isFinite(v) && v > 0 ? Math.min(1, v) : 0.3;
    } catch {
      return 0.3;
    }
  });
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [scrub, setScrub] = useState(null);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = volume;

    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/tracks`);
        const list = (await res.json()) || [];
        if (cancelled) return;
        const mapped = list.map((t) => ({ id: t.id, src: resolveSrc(t.url), title: t.title }));
        setTracks(mapped);
        setIndex(0);
        setTime(0);
        setDuration(0);
        if (mapped.length) {
          a.src = mapped[0].src;
          a.pause();
          setPlaying(false);
        }
      } catch {
        if (!cancelled) setTracks([]);
      }
    };
    load();
    window.addEventListener("nox-playlist-changed", load);
    return () => {
      cancelled = true;
      window.removeEventListener("nox-playlist-changed", load);
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    const a = audioRef.current;
    if (a) a.volume = muted ? 0 : volume;
  }, [volume, muted]);

  useEffect(() => {
    try {
      localStorage.setItem("nox_volume", String(volume));
    } catch {}
  }, [volume]);

  useEffect(() => {
    try {
      localStorage.setItem("nox_muted", muted ? "1" : "0");
    } catch {}
  }, [muted]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !TRACKS.length || !TRACKS[index]) return;
    a.src = TRACKS[index].src;
    setTime(0);
    setDuration(0);
    if (playing) a.play().catch(() => setPlaying(false));
  }, [index]); // eslint-disable-line

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("nox-now-playing", { detail: { index, playing, title: TRACKS[index]?.title } })
    );
  }, [index, playing, TRACKS]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play()
        .then(() => setPlaying(true))
        .catch(() => {});
    }
  };

  const skip = (dir) => {
    if (!TRACKS.length) return;
    setIndex((i) => Math.min(TRACKS.length - 1, Math.max(0, i + dir)));
  };

  const onEnded = () => {
    if (index < TRACKS.length - 1) setIndex(index + 1);
    else setPlaying(false);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 border-t border-line bg-[#0a0a0e]/95 backdrop-blur-xl"
      data-testid="player-bar"
    >
      <audio
        ref={audioRef}
        preload="metadata"
        onTimeUpdate={(e) => setTime(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onEnded={onEnded}
      />
      <div className="mx-auto max-w-7xl px-4 py-3 md:px-10">
        <div className="flex items-center gap-3 md:gap-4">
          <Disc3
            data-testid="player-disc"
            className={`h-5 w-5 shrink-0 text-gold ${playing ? "animate-spin" : ""}`}
            style={{ animationDuration: "5s" }}
          />
          <button
            onClick={toggle}
            data-testid="player-play"
            aria-label={playing ? "Pause" : "Play"}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gold/60 text-gold transition-all duration-300 hover:bg-gold/10"
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
          </button>
          <button
            onClick={() => skip(-1)}
            data-testid="player-prev"
            aria-label="Previous track"
            className="hidden h-8 w-8 shrink-0 items-center justify-center text-slate-500 transition-colors hover:text-silver sm:flex"
          >
            <SkipBack className="h-4 w-4" />
          </button>
          <button
            onClick={() => skip(1)}
            data-testid="player-next"
            aria-label="Next track"
            className="hidden h-8 w-8 shrink-0 items-center justify-center text-slate-500 transition-colors hover:text-silver sm:flex"
          >
            <SkipForward className="h-4 w-4" />
          </button>

          {TRACKS.length === 0 ? (
            <div
              className="!h-8 min-w-0 flex-1 border border-line bg-[#0d0d12] px-3 py-2 text-xs text-slate-600"
              data-testid="player-track-empty"
            >
              The set is silent — add tracks in the vault.
            </div>
          ) : (
            <Select value={String(index)} onValueChange={(v) => setIndex(Number(v))}>
              <SelectTrigger
                data-testid="player-track-select"
                className="!h-8 min-w-0 flex-1 border-line bg-[#0d0d12] text-xs text-silver"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-line bg-[#0d0d12]">
                {TRACKS.map((t, i) => (
                  <SelectItem key={t.id || i} value={String(i)} data-testid={`player-track-${i}`}>
                    {t.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="flex shrink-0 items-center gap-2">
            <button
              onClick={() => setMuted(!muted)}
              data-testid="player-mute"
              aria-label={muted ? "Unmute" : "Mute"}
              className="flex h-8 w-8 items-center justify-center text-slate-500 transition-colors hover:text-silver"
            >
              {muted ? <VolumeX className="h-4 w-4 text-gold" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <div className="hidden w-24 md:block" data-testid="player-volume-wrap">
              <Slider
                value={[muted ? 0 : Math.round(volume * 100)]}
                onValueChange={(v) => {
                  setVolume(v[0] / 100);
                  setMuted(false);
                }}
                max={100}
                step={1}
                data-testid="player-volume"
              />
            </div>
          </div>
        </div>

        <div className="mt-2 flex items-center gap-3">
          <span data-testid="player-time-current" className="w-9 font-jmono text-[10px] text-slate-500">
            {fmtTime(scrub !== null && duration ? (scrub / 100) * duration : time)}
          </span>
          <Slider
            value={[duration ? (scrub ?? (time / duration) * 100) : 0]}
            onValueChange={(v) => setScrub(v[0])}
            onValueCommit={(v) => {
              const a = audioRef.current;
              if (a && duration && Number.isFinite(v[0])) {
                const t = (v[0] / 100) * duration;
                a.currentTime = t;
                setTime(t);
              }
              setScrub(null);
            }}
            max={100}
            step={0.1}
            aria-label="Seek"
            data-testid="player-progress"
            className="flex-1"
          />
          <span data-testid="player-time-total" className="w-9 text-right font-jmono text-[10px] text-slate-500">
            {fmtTime(duration)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
